<template>
	<!-- Main section start -->
	<section
		id="home"
		class="min-h-screen relative overflow-hidden px-4 lg:px-32 pt-32 pb-24 bg-lime-100 text-secondary"
	>
		<div class="absolute inset-0 bg-[radial-gradient(circle_at_18%_22%,rgba(255,102,196,0.18),transparent_20%),radial-gradient(circle_at_80%_26%,rgba(177,161,237,0.16),transparent_24%),radial-gradient(circle_at_72%_82%,rgba(12,192,223,0.12),transparent_18%)]" aria-hidden="true"></div>

		<div
			:class="[
				'max-w-7xl mx-auto min-h-[calc(100vh-14rem)] relative grid items-center gap-10 lg:gap-14',
				hasHeroImage ? 'lg:grid-cols-[minmax(0,1fr)_minmax(360px,540px)]' : 'grid-cols-1'
			]"
		>
			<div
				:class="[
					'w-full flex flex-col justify-center px-2 py-6 md:px-0 md:py-10 relative',
					hasHeroImage
						? 'max-w-2xl'
						: 'max-w-3xl'
				]"
			>
				<div class="absolute -left-6 top-8 h-28 w-28 rounded-full bg-pink/18 blur-3xl" aria-hidden="true"></div>
				<div class="absolute right-6 bottom-10 h-24 w-24 rounded-full bg-blue/12 blur-3xl" aria-hidden="true"></div>

				<!-- Main text start -->
				<template v-if="isLoading">
					<LoadingSkeleton size="xl" class="mb-6"/>
					<LoadingSkeleton size="md" class="w-2/3 mb-3"/>
					<div class="flex mt-6 gap-4">
						<LoadingSkeleton size="md" class="w-10 h-10 rounded-full"/>
						<LoadingSkeleton size="md" class="w-10 h-10 rounded-full"/>
					</div>
				</template>
				<template v-else>
					<h1 class="sr-only">
						{{ header.title }}
					</h1>
					<div class="flex flex-col justify-center items-center md:items-start">
						<a href="#home" class="w-full max-w-[320px] md:max-w-[360px]">
							<img
								:src="logoLong"
								:alt="header.title || 'Dark Pollen'"
								class="w-full"
							>
						</a>
					</div>
					<p class="mt-6 text-xs font-semibold uppercase tracking-[0.26em] text-pink text-center md:text-left">
						Floral design for modern weddings
					</p>
					<p
						class="mt-5 max-w-xl text-lg md:text-xl font-medium leading-relaxed text-secondary text-center md:text-left"
					>
						{{ header.subtitle }}
					</p>
				</template>
				<!-- Main text end -->

				<!-- Social links start -->
				<div class="flex mt-6 gap-6 justify-center md:justify-start">
					<template v-if="isLoading">
						<LoadingSkeleton size="md" class="w-10 h-10 rounded-full"/>
						<LoadingSkeleton size="md" class="w-10 h-10 rounded-full"/>
					</template>
					<template v-else>
						<a
							:href="footer.instagram"
							target="_blank"
							rel="noopener noreferrer"
							aria-label="Instagram"
						>
							<i class="fa-brands fa-instagram socialIconDark transition"></i>
						</a>
						<a
							:href="footer.linkedin"
							target="_blank"
							rel="noopener noreferrer"
							aria-label="Facebook"
						>
							<i class="fa-brands fa-facebook socialIconDark transition"></i>
						</a>
					</template>
				</div>
				<!-- Social links end -->
			</div>

			<div v-if="hasHeroImage" class="relative w-full max-w-[460px] justify-self-center lg:max-w-[560px] lg:justify-self-end lg:-translate-y-10 lg:translate-x-6">
				<div class="absolute inset-x-10 top-12 h-[72%] hero-bloom-shadow bg-purple/20" aria-hidden="true"></div>
				<div class="absolute left-0 top-20 h-28 w-28 rounded-full bg-blue/18 blur-2xl" aria-hidden="true"></div>
				<div class="absolute right-0 top-4 h-32 w-32 rounded-full bg-pink/22 blur-2xl" aria-hidden="true"></div>
				<div class="absolute -right-4 bottom-10 h-36 w-36 rounded-full bg-yellow/18 blur-3xl" aria-hidden="true"></div>
				<div class="absolute left-8 bottom-2 h-10 w-28 rounded-full bg-secondary/18 blur-2xl" aria-hidden="true"></div>

				<div class="relative">
					<img
						:src="getHeroImageUrl()"
						:alt="header.title || 'Featured floral arrangement'"
						class="hero-bloom-mask h-[340px] w-full object-cover shadow-[0_28px_80px_rgba(177,161,237,0.24)] sm:h-[430px] lg:h-[620px]"
					>
				</div>
			</div>
		</div>

		<!-- "More" button start -->
		<div class="absolute bottom-8 left-0 right-0 flex justify-center items-center">
			<a href="#about-section" class="animate-bounce">
				<i
					:class="[
						'fa-solid fa-arrow-down transition text-xl rounded-full shadow p-4 hover:-translate-y-2 hover:shadow-xl',
						'text-stone-100 bg-secondary'
					]"
				></i>
			</a>
		</div>
		<!-- "More" button end -->
	</section>
	<!-- Main section end -->
</template>

<script>
import { contentfulClient } from '@/config/contentful'
import LoadingSkeleton from '@/components/LoadingSkeleton.vue'
import logoLong from '@/assets/logo-long.png'

export default {
	name: 'MainSection',
	components: {
		LoadingSkeleton
	},
	data() {
		return {
			isLoading: true,
			header: {
				title: '',
				subtitle: '',
				image: null
			},
			footer: {
				instagram: '',
				linkedin: ''
			},
			logoLong
		}
	},
	computed: {
		hasHeroImage() {
			return Boolean(this.header.image?.fields?.file?.url)
		}
	},
	methods: {
		getHeroImageUrl() {
			const imageUrl = this.header.image?.fields?.file?.url
			if (!imageUrl) {
				return ''
			}

			const normalizedUrl = imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl
			const optimizedUrl = new URL(normalizedUrl)
			optimizedUrl.searchParams.set('fm', 'webp')
			optimizedUrl.searchParams.set('q', '82')
			optimizedUrl.searchParams.set('w', '2200')
			optimizedUrl.searchParams.set('fit', 'fill')
			return optimizedUrl.toString()
		}
	},
	async created() {
		try {
			const [headerEntry, footerEntry] = await Promise.all([
				contentfulClient.getEntries({
					content_type: 'header',
					limit: 1
				}),
				contentfulClient.getEntries({
					content_type: 'footer',
					limit: 1
				})
			])

			if (headerEntry.items && headerEntry.items.length > 0 && headerEntry.items[0].fields) {
				this.header = {
					title: headerEntry.items[0].fields.title || '',
					subtitle: headerEntry.items[0].fields.subtitle || '',
					image: headerEntry.items[0].fields.image || null
				}
			}

			if (footerEntry.items && footerEntry.items.length > 0 && footerEntry.items[0].fields) {
				this.footer = footerEntry.items[0].fields
			}
		} catch (error) {
			console.error('Error fetching content:', error)
		} finally {
			this.isLoading = false
		}
	}
}
</script>

<style scoped>
.hero-bloom-mask {
	border-radius: 44% 56% 39% 61% / 36% 44% 56% 64%;
	transition: border-radius 0.8s ease, transform 0.8s ease;
	transform: rotate(4deg);
	transform-origin: center center;
}

.hero-bloom-shadow {
	border-radius: 48% 52% 34% 66% / 38% 42% 58% 62%;
	filter: blur(26px);
	transform: rotate(-8deg);
}

.hero-bloom-mask:hover {
	border-radius: 58% 42% 46% 54% / 40% 54% 46% 60%;
	transform: rotate(1deg) scale(1.02);
}
@media (max-width: 1023px) {
	.hero-bloom-mask {
		transform: rotate(2deg);
	}
}
</style>
